import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../lib/useFeatures";
import { StatusBadge, SeverityDot, AccessBadge, ReservedBadge, EnforcementBadge } from "../components/ui-helpers";
import { ArrowLeft, Sparkle, Scales, Lock, Paperclip, BookOpen, CaretDown, CaretRight, Warning, UsersThree } from "@phosphor-icons/react";
import GlossaryText from "../components/GlossaryText";
import EvidenceChecklist from "../components/EvidenceChecklist";

export default function RequirementDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit, isRole, aiEnabled , aiConfidentialBlocked } = useAuth();
  const aiOff = !aiEnabled || aiConfidentialBlocked;
  const aiOffTitle = !aiEnabled ? "AI features are turned off for this entity" : aiConfidentialBlocked ? "Evidence suggestions read your evidence repository and are blocked while AI is set to Public-information only" : undefined;
  const { can } = useFeatures();
  const [data, setData] = useState(null);
  const [aiSuggest, setAiSuggest] = useState([]);
  const [loadingAi, setLoadingAi] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [prevTerm, setPrevTerm] = useState({}); // term id -> bool expanded
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const ROLE_FIELDS = [["sme_primary", "SME Primary"], ["sme_backup", "SME Backup"], ["compliance_primary", "Compliance Primary"], ["compliance_backup", "Compliance Backup"]];
  const [roles, setRoles] = useState({ sme_primary: "", sme_backup: "", compliance_primary: "", compliance_backup: "" });

  const load = useCallback(() => {
    api.get(`/requirements/${id}`).then((r) => {
      setData(r.data);
      const rq = r.data.requirement;
      setRoles({ sme_primary: rq.sme_primary || "", sme_backup: rq.sme_backup || "", compliance_primary: rq.compliance_primary || "", compliance_backup: rq.compliance_backup || "" });
    });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveRoles = async () => {
    try {
      await api.put(`/requirements/${id}`, { ...data.requirement, ...roles });
      toast.success("Assignments updated");
      load();
    } catch (e) { toast.error("Could not save assignments"); }
  };

  const suggestEvidence = async () => {
    setLoadingAi(true);
    try {
      const { data: res } = await api.post(`/gaps/${id}/ai-suggest`);
      setAiSuggest(res.suggestions);
    } catch (e) { toast.error("AI suggestion failed"); }
    finally { setLoadingAi(false); }
  };

  const setHold = async (active) => {
    if (active && !holdReason.trim()) return toast.error("Enter a reason for the legal hold");
    try {
      await api.post(`/requirements/${id}/legal-hold`, { active, reason: active ? holdReason : "" });
      toast.success(active ? "Legal hold placed" : "Legal hold released");
      setHoldReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 font-mono text-sm">Loading…</div>;
  const { requirement: r, controls, documents, evidence, narrative, gaps } = data;
  const terms = data.terms || [];
  const cfr = data.cfr;
  const cfrForPart = (label) => {
    if (!cfr?.present || !cfr.parts) return null;
    const num = (String(label || "").match(/(\d+(?:\.\d+)*)/) || [])[1];
    return num ? cfr.parts[num] : null;
  };
  const access = r.access || {};
  const reqStatus = access.status || r.status || "currently_enforceable";
  const enforceLabel = reqStatus === "future_enforceable" ? "Becomes enforceable"
    : reqStatus === "superseded" ? "Superseded — was effective"
    : reqStatus === "retired" ? "Retired" : "Enforceable since";
  const gated = !!access.gated; // category2_starter lockout: read-only (Phase 2 — no 403 yet)
  const lockMsg = access.state === "historical"
    ? "This Requirement is retired and outside active enforcement — it is read-only for backward audits."
    : "This Requirement is outside your current Registration scope. Upgrade your plan to unlock the full active NERC Compliance suite.";

  return (
    <div>
      <div className="border-b border-border px-8 py-5 bg-card sticky top-0 z-10">
        <button data-testid="back-btn" onClick={() => nav("/requirements")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"><ArrowLeft size={14} /> Requirements</button>
        <div className="flex items-center gap-3">
          <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5">{r.standard_id} {r.req_number}</span>
          {gaps.length === 0 ? <StatusBadge status="approved" /> : <span className="border border-red-300 bg-red-50 text-red-700 px-2 py-0.5 text-[11px] font-semibold uppercase">{gaps.length} gaps</span>}
          {r.legal_hold && <span data-testid="legal-hold-badge" className="flex items-center gap-1 border border-amber-500 bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-semibold uppercase"><Scales size={12} weight="fill" />Legal Hold</span>}
          <AccessBadge access={access} data-testid="req-detail-access-badge" />
          {r.reserved && <ReservedBadge data-testid="req-detail-reserved-badge" />}
          <EnforcementBadge status={reqStatus} effectiveDate={r.effective_date} data-testid="req-detail-enforcement-badge" />
        </div>
        <div data-testid="req-enforcement-date" className="mt-2 text-xs text-muted-foreground">
          {r.effective_date
            ? <span><span className="font-semibold text-foreground">{enforceLabel}</span> {r.effective_date}</span>
            : reqStatus === "retired" ? "Retired" : "Enforcement date not set"}
        </div>
        {gated && (
          <div data-testid="req-locked-banner" className="mt-3 flex items-start gap-2 border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 max-w-3xl">
            <Lock size={18} className="mt-0.5 shrink-0" />
            <span>{lockMsg}</span>
          </div>
        )}
        <p className="text-sm mt-3 max-w-3xl"><GlossaryText text={r.text} /></p>
        {r.measure && (
          <div className="mt-3 max-w-3xl border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/40 pl-3 py-2" data-testid="req-measure">
            <span className="overline text-emerald-700 dark:text-emerald-300 font-bold">Measure — what auditors look for</span>
            <p className="text-sm mt-0.5 whitespace-pre-line"><GlossaryText text={r.measure} /></p>
          </div>
        )}
        {cfr?.present && cfr.requirement && (
          <div className="max-w-3xl"><CfrCallout entry={cfr.requirement} meta={cfr} testid="req-cfr" /></div>
        )}
        {r.applicable_functions?.length > 0 && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="req-functions">
            <span className="overline text-muted-foreground mr-1">Applies to</span>
            {r.applicable_functions.map((f) => (
              <span key={f} className="font-mono text-[11px] border border-border bg-secondary px-1.5 py-0.5">{f}</span>
            ))}
          </div>
        )}
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <EvidenceChecklist
            requirementId={id}
            hasMeasure={!!(r.measure || (r.sub_requirements || []).some((sp) => sp.measure))}
            canEdit={canEdit}
            aiEnabled={aiEnabled}
          />
          {r.sub_requirements?.length > 0 && (
            <Section title="Requirement Parts / Sections" count={r.sub_requirements.length}>
              {(r.standard_id || "").toUpperCase().startsWith("CIP") ? (
                // NERC CIP-style table: "Applicable Systems" is the first column,
                // then Part / Text (+ Measure) / Impact chips.
                <div className="overflow-x-auto" data-testid="cip-parts-table">
                  <table className="w-full text-sm border border-border">
                    <thead className="bg-secondary">
                      <tr>
                        <th className="text-left align-top px-3 py-2 border-b border-border w-72 font-semibold">Applicable Systems</th>
                        <th className="text-left align-top px-3 py-2 border-b border-border w-16 font-semibold">Part</th>
                        <th className="text-left align-top px-3 py-2 border-b border-border font-semibold">Requirement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.sub_requirements.map((sp, i) => {
                        const linked = (evidence || []).filter((ev) => (ev.linked_parts || []).some((p) => p.requirement_id === id && p.label === sp.label));
                        return (
                          <tr key={sp.label || i} className="border-b border-border last:border-0 align-top" data-testid={`req-part-${i}`}>
                            <td className="px-3 py-2 text-xs">
                              {(sp.applicable_systems || []).length > 0 ? (
                                <ul className="list-disc pl-4 space-y-0.5" data-testid={`part-applicable-systems-${i}`}>
                                  {sp.applicable_systems.map((s, si) => (<li key={si}>{s}</li>))}
                                </ul>
                              ) : (
                                <span className="text-muted-foreground italic">—</span>
                              )}
                              {(sp.impact_levels || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {sp.impact_levels.map((im) => (
                                    <span key={im} className="text-[10px] uppercase tracking-wide border border-border px-1.5 py-0.5 text-muted-foreground">{im} impact</span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-mono font-bold text-xs bg-primary text-primary-foreground px-1.5 py-0.5" data-testid={`part-label-${i}`}>{sp.label}</span>
                              {sp.effective_date && (
                                <div className="mt-1.5">
                                  <span data-testid={`part-effective-${i}`} className="text-[10px] uppercase tracking-wide border border-blue-200 bg-blue-50 text-blue-700 px-1.5 py-0.5">
                                    Enforceable {sp.effective_date}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <p className="whitespace-pre-line"><GlossaryText text={sp.text} /></p>
                              {sp.measure && (
                                <div className="mt-1.5 border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/40 pl-2 py-1" data-testid={`part-measure-${i}`}>
                                  <span className="overline text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">Measure</span>
                                  <p className="text-xs mt-0.5 whitespace-pre-line"><GlossaryText text={sp.measure} /></p>
                                </div>
                              )}
                              {cfrForPart(sp.label) && (
                                <CfrCallout entry={cfrForPart(sp.label)} meta={cfr} size="sm" testid={`part-cfr-${i}`} />
                              )}
                              {linked.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2" data-testid={`part-evidence-${i}`}>
                                  {linked.map((ev) => (
                                    <span key={ev.id} className="inline-flex items-center gap-1 text-[10px] border border-emerald-300 bg-emerald-50 text-emerald-800 px-1.5 py-0.5">
                                      <Paperclip size={11} />{ev.name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                r.sub_requirements.map((sp, i) => (
                  <div key={sp.label || i} className="border-b border-border py-3 last:border-0" data-testid={`req-part-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-xs bg-primary text-primary-foreground px-1.5 py-0.5">{sp.label}</span>
                      {(sp.impact_levels || []).map((im) => (
                        <span key={im} className="text-[10px] uppercase tracking-wide border border-border px-1.5 py-0.5 text-muted-foreground">{im} impact</span>
                      ))}
                      {sp.effective_date && (
                        <span data-testid={`part-effective-${i}`} className="text-[10px] uppercase tracking-wide border border-blue-200 bg-blue-50 text-blue-700 px-1.5 py-0.5">
                          Enforceable {sp.effective_date}
                        </span>
                      )}
                    </div>
                    <p className="text-sm mt-1.5 whitespace-pre-line"><GlossaryText text={sp.text} /></p>
                    {sp.measure && (
                      <div className="mt-1.5 border-l-2 border-emerald-400 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/40 pl-2 py-1" data-testid={`part-measure-${i}`}>
                        <span className="overline text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">Measure</span>
                        <p className="text-xs mt-0.5 whitespace-pre-line"><GlossaryText text={sp.measure} /></p>
                      </div>
                    )}
                    {cfrForPart(sp.label) && (
                      <CfrCallout entry={cfrForPart(sp.label)} meta={cfr} size="sm" testid={`part-cfr-${i}`} />
                    )}
                    {(() => {
                      const linked = (evidence || []).filter((ev) => (ev.linked_parts || []).some((p) => p.requirement_id === id && p.label === sp.label));
                      return linked.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-2" data-testid={`part-evidence-${i}`}>
                          {linked.map((ev) => (
                            <span key={ev.id} className="inline-flex items-center gap-1 text-[10px] border border-emerald-300 bg-emerald-50 text-emerald-800 px-1.5 py-0.5">
                              <Paperclip size={11} />{ev.name}
                            </span>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                ))
              )}
            </Section>
          )}

          {terms.length > 0 && (
            <div className="border border-indigo-200 bg-indigo-50/30" data-testid="req-glossary-panel">
              <button data-testid="req-glossary-toggle" onClick={() => setGlossaryOpen((v) => !v)} className="w-full border-b border-indigo-200 bg-indigo-100/40 px-4 py-3 flex items-center gap-2 text-left">
                {glossaryOpen ? <CaretDown size={15} weight="bold" className="text-indigo-700 shrink-0" /> : <CaretRight size={15} weight="bold" className="text-indigo-700 shrink-0" />}
                <BookOpen size={16} weight="fill" className="text-indigo-700" />
                <span className="font-display font-bold text-[#071B3E]">Glossary &amp; Key Terms</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">— defined terms this standard relies on</span>
                <span className="ml-auto font-mono text-xs bg-indigo-700 text-white px-2 py-0.5">{terms.length}</span>
                {!glossaryOpen && <span className="text-[11px] text-indigo-700 font-semibold ml-2">Show</span>}
              </button>
              {glossaryOpen && (
              <div className="divide-y divide-indigo-100">
                {terms.map((t) => (
                  <div key={t.id} data-testid={`req-term-${t.id}`} className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-[#071B3E]">{t.term}</span>
                      {t.is_new_introduction && <span data-testid={`req-term-new-${t.id}`} className="text-[10px] uppercase tracking-wide border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5 font-bold">New Term</span>}
                      {t.has_future_revision && <span data-testid={`req-term-revised-${t.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide border border-orange-400 bg-orange-50 text-orange-700 px-1.5 py-0.5 font-bold"><Warning size={11} weight="fill" />Revised · eff {t.upcoming?.effective_date}</span>}
                      {t.is_referenced && !t.is_new_introduction && !t.has_future_revision && <span data-testid={`req-term-ref-${t.id}`} className="text-[10px] uppercase tracking-wide border border-border bg-secondary text-muted-foreground px-1.5 py-0.5">Used here</span>}
                      {t.effective_date && <span className="text-[10px] font-mono text-muted-foreground">eff {t.effective_date}</span>}
                    </div>
                    {t.definition && <p className="text-sm text-muted-foreground mt-1">{t.definition}</p>}
                    {t.has_future_revision && t.upcoming && (
                      <div className="mt-1.5 border-l-2 border-orange-400 bg-orange-50/50 pl-3 py-1.5">
                        <button data-testid={`req-term-prev-btn-${t.id}`} onClick={() => setPrevTerm((p) => ({ ...p, [t.id]: !p[t.id] }))}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:underline">
                          {prevTerm[t.id] ? <CaretDown size={12} /> : <CaretRight size={12} />}Revised definition effective {t.upcoming.effective_date} — view what changes
                        </button>
                        {t.upcoming.change_summary && (
                          <p data-testid={`req-term-change-${t.id}`} className="mt-1 text-xs text-foreground/80"><span className="font-bold uppercase tracking-wide text-orange-700 text-[10px] mr-1">What changes:</span>{t.upcoming.change_summary}</p>
                        )}
                        {prevTerm[t.id] && (
                          <div data-testid={`req-term-prev-${t.id}`} className="mt-1.5 grid sm:grid-cols-2 gap-2 text-xs">
                            <div className="border border-border bg-white p-2">
                              <div className="font-bold text-[10px] uppercase text-green-700 mb-0.5">Current · eff {t.effective_date || "—"}</div>
                              <p className="text-muted-foreground">{t.definition}</p>
                            </div>
                            <div className="border border-orange-300 bg-orange-50/60 p-2">
                              <div className="font-bold text-[10px] uppercase text-orange-700 mb-0.5">Upcoming · eff {t.upcoming.effective_date}</div>
                              <p className="text-foreground/80">{t.upcoming.definition}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          <Section title="Linked Controls" count={controls.length}>
            {controls.map((c) => (
              <div key={c.id} className="border-b border-border py-2.5" data-testid={`req-control-${c.id}`}>
                <div className="font-medium text-sm">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.frequency} · Owner: {c.owner || "—"}</div>
              </div>
            ))}
            {controls.length === 0 && <Hint text="No controls mapped. Map a control from the Controls page." />}
          </Section>

          <Section title="Linked Evidence" count={evidence.length}>
            {evidence.map((e) => (
              <div key={e.id} className="border-b border-border py-2.5" data-testid={`req-evidence-${e.id}`}>
                <div className="font-medium text-sm">{e.name}</div>
                <div className="text-xs text-muted-foreground">{e.evidence_type} · {e.period_start || "?"} → {e.period_end || "?"}</div>
              </div>
            ))}
            {evidence.length === 0 && <Hint text="No evidence attached." />}
          </Section>

          <Section title="Linked Documents" count={documents.length}>
            {documents.map((dd) => (
              <button key={dd.id} onClick={() => nav(`/documents/${dd.id}`)} className="block w-full text-left border-b border-border py-2.5 hover:bg-secondary/40">
                <div className="font-medium text-sm">{dd.title}</div>
                <div className="text-xs text-muted-foreground capitalize">{dd.doc_type}</div>
              </button>
            ))}
            {documents.length === 0 && <Hint text="No documents linked." />}
          </Section>
        </div>

        <div className="space-y-6">
          <div className="border border-red-300 bg-card" data-testid="gaps-panel">
            <div className="border-b border-red-300 bg-red-50 px-4 py-3 font-display font-bold text-red-700">Missing Evidence ({gaps.length})</div>
            <div className="p-4 space-y-3">
              {gaps.length === 0 && <p className="text-sm text-green-700">No gaps detected. Fully supported.</p>}
              {gaps.map((g, i) => (
                <div key={`${g.type}-${i}`} className="flex gap-2 items-start">
                  <SeverityDot severity={g.severity} />
                  <div>
                    <p className="text-sm">{g.message}</p>
                    {g.prompt && <p className="text-xs text-muted-foreground mt-0.5" data-testid={`gap-prompt-detail-${i}`}>→ {g.prompt}</p>}
                  </div>
                </div>
              ))}
              {canEdit && !gated && (
                <div className="pt-2 border-t border-red-200" data-testid="role-assign-editor">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Assignments — SMEs do the work, Compliance verifies</div>
                  <div className="grid grid-cols-1 gap-2">
                    {ROLE_FIELDS.map(([key, label]) => (
                      <div key={key}>
                        <label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</label>
                        <input data-testid={`role-input-${key}`} value={roles[key]} onChange={(e) => setRoles({ ...roles, [key]: e.target.value })}
                          placeholder={label} className="w-full border border-border px-2 py-1 text-sm mt-0.5" />
                      </div>
                    ))}
                  </div>
                  <button data-testid="save-roles-btn" onClick={saveRoles} className="mt-2 w-full border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white">Save assignments</button>
                </div>
              )}
              {canEdit && !gated && (
                <button data-testid="ai-suggest-evidence-btn" onClick={suggestEvidence} disabled={loadingAi || aiOff} title={aiOffTitle}
                  className="mt-2 flex items-center gap-2 border border-black px-3 py-1.5 text-sm font-semibold hover:bg-primary hover:text-white disabled:opacity-50">
                  <Sparkle size={14} weight="fill" />{loadingAi ? "Thinking…" : "AI: suggest evidence types"}
                </button>
              )}
              {canEdit && gated && (
                <p data-testid="role-assign-locked" className="pt-2 border-t border-red-200 text-xs text-muted-foreground flex items-start gap-1.5">
                  <Lock size={14} className="shrink-0 mt-0.5" />Assignments &amp; AI drafting are read-only for this requirement under your current plan.
                </p>
              )}
              {aiSuggest.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-sm space-y-1" data-testid="ai-evidence-suggestions">
                  {aiSuggest.map((s, i) => <li key={`${i}-${s}`}>{s}</li>)}
                </ul>
              )}
            </div>
          </div>

          <div className="border border-border bg-card">
            <div className="border-b border-border px-4 py-3 font-display font-bold">Build the Ps</div>
            <div className="p-4">
              <p className="text-sm text-muted-foreground">Run applicability and generate the Policy → Procedure → Control chain.</p>
              <button data-testid="build-ps-btn" onClick={() => nav(`/build?req=${id}`)} disabled={gated}
                className={`mt-3 block w-full border border-black bg-primary text-primary-foreground px-3 py-1.5 text-sm font-semibold hover:bg-primary/90 ${gated ? "opacity-40 cursor-not-allowed" : ""}`}>
                Build the Ps for this requirement →
              </button>
            </div>
          </div>

          <div className="border border-border bg-card">
            <div className="border-b border-border px-4 py-3 font-display font-bold">RSAW Narrative</div>
            <div className="p-4">
              {narrative ? <StatusBadge status={narrative.status} /> : <span className="text-sm text-muted-foreground">No narrative yet.</span>}
              <button data-testid="open-rsaw-btn" onClick={() => nav(`/rsaw?req=${id}`)} disabled={gated}
                className={`mt-3 block w-full border border-black px-3 py-1.5 text-sm font-semibold hover:bg-primary hover:text-white ${gated ? "opacity-40 cursor-not-allowed" : ""}`}>
                {gated ? "Locked — read-only" : "Open in RSAW Workspace →"}
              </button>
            </div>
          </div>

          {isRole("admin") && (
            <div className={`border ${r.legal_hold ? "border-amber-500" : "border-border"} bg-card`} data-testid="legal-hold-panel">
              <div className={`border-b px-4 py-3 font-display font-bold flex items-center gap-2 ${r.legal_hold ? "border-amber-500 bg-amber-50 text-amber-800" : "border-border"}`}>
                <Scales size={16} weight={r.legal_hold ? "fill" : "regular"} />Legal Hold
              </div>
              <div className="p-4 space-y-3">
                {!can("legal_hold") ? (
                  <div data-testid="legal-hold-locked" className="space-y-2">
                    <p className="text-sm text-muted-foreground flex items-start gap-1.5">
                      <Lock size={16} className="shrink-0 mt-0.5" />
                      Tenant-scoped Legal Holds are a <span className="font-semibold">Fleet plan</span> feature.
                    </p>
                    <button data-testid="legal-hold-upgrade-btn" onClick={() => nav("/admin?tab=billing")}
                      className="w-full border border-amber-600 text-amber-700 px-3 py-1.5 text-sm font-semibold hover:bg-amber-600 hover:text-white">Upgrade to Fleet</button>
                  </div>
                ) : r.legal_hold ? (
                  <>
                    <p className="text-sm">This requirement is on <span className="font-semibold text-amber-700">legal hold</span>. All related evidence, documents, controls, narratives, forms and tasks are protected from the Clear/purge tools.</p>
                    {r.legal_hold_reason && <p className="text-sm" data-testid="legal-hold-reason"><span className="font-semibold">Reason:</span> {r.legal_hold_reason}</p>}
                    <p className="text-xs text-muted-foreground font-mono">Placed by {r.legal_hold_by || "—"}{r.legal_hold_at ? ` · ${r.legal_hold_at.slice(0, 16).replace("T", " ")}` : ""}</p>
                    <button data-testid="release-hold-btn" onClick={() => setHold(false)}
                      className="w-full border border-amber-600 text-amber-700 px-3 py-1.5 text-sm font-semibold hover:bg-amber-600 hover:text-white">Release Legal Hold</button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">Place a legal hold to protect this requirement and everything linked to it from being cleared or purged.</p>
                    <input data-testid="hold-reason-input" value={holdReason} onChange={(e) => setHoldReason(e.target.value)}
                      placeholder="Reason (e.g. litigation hold #2026-04)" className="w-full border border-border px-2 py-1 text-sm" />
                    <button data-testid="place-hold-btn" onClick={() => setHold(true)}
                      className="w-full border border-amber-600 bg-amber-600 text-white px-3 py-1.5 text-sm font-semibold hover:bg-amber-700">Place Legal Hold</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div className="border border-border bg-card">
      <div className="border-b border-border px-4 py-3 flex items-center justify-between">
        <span className="font-display font-bold">{title}</span>
        <span className="font-mono text-sm text-muted-foreground">{count}</span>
      </div>
      <div className="px-4 py-2">{children}</div>
    </div>
  );
}
const Hint = ({ text }) => <p className="text-sm text-muted-foreground py-2">{text}</p>;

const CFR_TYPE_CLS = {
  sole: "border-sky-400 dark:border-sky-600 bg-sky-50/70 dark:bg-sky-950/40 text-sky-800 dark:text-sky-200",
  shared_both: "border-violet-400 dark:border-violet-600 bg-violet-50/70 dark:bg-violet-950/40 text-violet-800 dark:text-violet-200",
  split: "border-amber-400 dark:border-amber-600 bg-amber-50/70 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200",
  not_applicable: "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-300",
  unspecified: "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-300",
};

function CfrCallout({ entry, meta, size, testid }) {
  if (!entry) return null;
  const sm = size === "sm";
  const cls = CFR_TYPE_CLS[entry.responsibility_type] || CFR_TYPE_CLS.unspecified;
  const mine = meta?.my_entity_party;
  return (
    <div data-testid={testid} className={`${sm ? "mt-1.5 pl-2 py-1.5" : "mt-3 pl-3 py-2"} border-l-2 ${cls}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <UsersThree size={sm ? 12 : 14} weight="fill" />
        <span className={`overline font-bold ${sm ? "text-[10px]" : ""}`}>CFR — {entry.responsibility_type_label || "Responsibility"}</span>
        {meta?.registered_function && <span className={`${sm ? "text-[9px]" : "text-[10px]"} font-mono opacity-70`}>· {meta.registered_function}</span>}
      </div>
      {entry.assignments?.length > 0 ? (
        <div className={`flex items-center gap-1.5 flex-wrap ${sm ? "mt-1" : "mt-1.5"}`}>
          <span className={`${sm ? "text-[10px]" : "text-xs"} font-semibold opacity-80`}>Performed by:</span>
          {entry.assignments.map((a) => (
            <span key={a} data-testid={`${testid}-party-${a}`}
              className={`${sm ? "text-[10px]" : "text-xs"} font-semibold border px-1.5 py-0.5 rounded-none ${a === mine ? "border-current bg-current/10 ring-1 ring-current" : "border-current/40"}`}>
              {a}{a === mine ? " (you)" : ""}
            </span>
          ))}
        </div>
      ) : (
        <p className={`${sm ? "text-[10px]" : "text-xs"} opacity-80 mt-1`}>
          {entry.responsibility_type === "not_applicable" ? "Not applicable to your entity under this coordinated registration." : "No party assigned."}
        </p>
      )}
      {entry.responsibility_details && (
        <p className={`${sm ? "text-[11px]" : "text-xs"} opacity-80 mt-1 whitespace-pre-line`}>{entry.responsibility_details}</p>
      )}
    </div>
  );
}

